import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import AcessoCandidatoConfiguracaoViewModel, {
  AcessoCandidatoConfiguracao,
} from '../../viewmodel/AcessoCandidatoConfiguracaoViewModel';

const AcessoCandidatoConfiguracoesPage: React.FC = () => {
  const [configuracao, setConfiguracao] = useState<AcessoCandidatoConfiguracao | null>(null);
  const [janelaHoras, setJanelaHoras] = useState<number>(3);
  const [envioEmail, setEnvioEmail] = useState<boolean>(true);
  const [validacaoPorHistorico, setValidacaoPorHistorico] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await AcessoCandidatoConfiguracaoViewModel.buscar();
      setConfiguracao(data);
      if (data) {
        setJanelaHoras(data.janelaLiberacaoHoras);
        setEnvioEmail(data.envioFormularioRecursoPorEmail);
        setValidacaoPorHistorico(data.validacaoPorHistoricoHabilitada);
      }
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao carregar configurações de acesso do candidato.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleSalvar = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!Number.isFinite(janelaHoras) || janelaHoras <= 0) {
      toast.error('Informe uma quantidade de horas válida (maior que zero).');
      return;
    }

    setSaving(true);
    try {
      const salvo = await AcessoCandidatoConfiguracaoViewModel.salvar({
        janelaLiberacaoHoras: janelaHoras,
        envioFormularioRecursoPorEmail: envioEmail,
        validacaoPorHistoricoHabilitada: validacaoPorHistorico,
      });
      setConfiguracao(salvo);
      toast.success('Configurações de acesso do candidato atualizadas.');
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Erro ao salvar configurações de acesso do candidato.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Configurações de Acesso do Candidato</h2>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Form onSubmit={handleSalvar}>
          {!configuracao && (
            <Alert variant="warning" className="mb-4">
              Nenhuma configuração encontrada. Preencha os dados abaixo para criar.
            </Alert>
          )}

          <Row>
            <Col md={12} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-clock-history fs-3 me-3 text-primary"></i>
                    <Card.Title className="mb-0">Janela de Liberação</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-3">
                    Tempo de antecedência para liberar o acesso à <strong>Sala de Espera</strong> antes da banca virtual.
                  </Card.Text>
                  <Form.Group>
                    <Form.Label>Horas de antecedência</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={janelaHoras}
                      onChange={(e) => setJanelaHoras(Number(e.target.value))}
                    />
                    <Form.Text className="text-muted">
                      Ex: 3 horas antes do horário da banca
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={12} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-envelope-paper fs-3 me-3 text-success"></i>
                    <Card.Title className="mb-0">Formulário de Recurso</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-3">
                    Habilita o envio automático por e-mail do link para o <strong>Formulário de Recurso</strong>.
                  </Card.Text>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="envio-recurso-email"
                      label="Enviar formulário por e-mail"
                      checked={envioEmail}
                      onChange={(e) => setEnvioEmail(e.target.checked)}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={12} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-check-circle fs-3 me-3 text-info"></i>
                    <Card.Title className="mb-0">Validação por Histórico</Card.Title>
                  </div>
                  <Card.Text className="text-muted mb-3">
                    <strong>Valida</strong> automaticamente candidatos com <strong>histórico</strong> de aprovação em processos anteriores.
                  </Card.Text>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="validacao-historico"
                      label="Validar por histórico"
                      checked={validacaoPorHistorico}
                      onChange={(e) => setValidacaoPorHistorico(e.target.checked)}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-grid">
            <Button type="submit" size="lg" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar configurações'}
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
};

export default AcessoCandidatoConfiguracoesPage;
